import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { TopBar } from '../components/TopBar';
import { CreateTicketModal } from '../components/CreateTicketModal';
import { useAuth } from '../hooks/useAuth';
import { api, directoryApi, type DirectoryAgent } from '../lib/api';
import { errorMessage } from '../lib/api-error';
import type { Ticket, TicketPriority, TicketStatus } from '../lib/types';
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  StickyNote,
  X,
} from 'lucide-react';

const STATUSES: TicketStatus[] = ['open', 'in_progress', 'waiting_customer', 'resolved', 'closed'];
const PRIORITIES: TicketPriority[] = ['LOW', 'MEDIUM', 'HIGH', 'URGENT'];

const STATUS_LABELS: Record<TicketStatus, string> = {
  open: 'Open',
  in_progress: 'In progress',
  waiting_customer: 'Waiting on customer',
  resolved: 'Resolved',
  closed: 'Closed',
};

const STATUS_STYLES: Record<TicketStatus, string> = {
  open: 'bg-amber-50 border-amber-300 text-amber-800',
  in_progress: 'bg-primary-50 border-primary-300 text-primary-700',
  waiting_customer: 'bg-purple-50 border-purple-300 text-purple-700',
  resolved: 'bg-green-50 border-green-300 text-green-700',
  closed: 'bg-gray-50 border-gray-300 text-gray-600',
};

const PRIORITY_STYLES: Record<TicketPriority, string> = {
  LOW: 'bg-white border-gray-300 text-gray-600',
  MEDIUM: 'bg-white border-gray-300 text-gray-700',
  HIGH: 'bg-orange-50 border-orange-300 text-orange-700',
  URGENT: 'bg-red-50 border-red-300 text-red-700',
};

/**
 * Which worklist a row is shown in. NOT the same vocabulary as the doc's
 * `source`: rows are written by several services and carry values this console
 * never writes ('ai' when the support bot opened one from a chat, 'mobile-app'
 * when the customer did it in the app), so the page buckets rather than filters.
 * 'other' is the catch-all — a source nobody here has heard of still lists.
 */
type TabKey = 'internal' | 'customer' | 'other';

/** Doc `source` values that mean "raised for a subscriber", whoever typed it.
 *  'mobile_app' is defensive: another writer may use the underscore spelling. */
const CUSTOMER_SOURCES: ReadonlySet<string> = new Set(['customer', 'ai', 'mobile-app', 'mobile_app']);

/** Provenance chip copy for the sources this console does NOT write itself
 *  ('customer' and 'internal' need no chip — the tab already says it). An
 *  unlisted value falls back to the raw string rather than to nothing. */
const SOURCE_LABELS: Record<string, string> = {
  ai: 'Opened by AI',
  'mobile-app': 'From the mobile app',
  mobile_app: 'From the mobile app',
};

const TABS: Array<{ key: TabKey; label: string; hint: string }> = [
  {
    key: 'internal',
    label: 'Team issues',
    hint: "The team's own issue list — what the tracking spreadsheet used to hold.",
  },
  {
    key: 'customer',
    label: 'Customer tickets',
    hint: 'Raised for a subscriber — by an agent, by the AI, or from the mobile app.',
  },
  {
    key: 'other',
    label: 'Other sources',
    hint: 'Tickets whose source this console does not recognise. They are listed here rather than hidden.',
  },
];

/** GENERAL → General, PLAN_CHANGE → Plan change, LOW → Low. */
function pretty(v: string): string {
  const s = v.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Timestamps arrive as ISO strings from the serializer; epoch ms tolerated. */
function toMs(v?: string | number | null): number | null {
  if (v == null) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  const ms = Date.parse(v);
  return Number.isNaN(ms) ? null : ms;
}

function formatRelative(v?: string | number | null): string {
  const ms = toMs(v);
  if (ms === null) return '--';
  const diff = Date.now() - ms;
  if (diff < 60_000) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d} day${d === 1 ? '' : 's'} ago`;
  const mo = Math.floor(d / 30);
  return `${mo} month${mo === 1 ? '' : 's'} ago`;
}

/** The doc field is `assignedToId`; the PATCH body calls it `assigneeUid`. */
function assigneeOf(t: Ticket): string {
  return t.assignedToId || t.assigneeUid || '';
}

/** The doc's raw `source`, cased down. '' when the field is missing entirely. */
function sourceOf(t: Ticket): string {
  return String(t.source ?? '').trim().toLowerCase();
}

/**
 * Which tab a row belongs in. Every row lands SOMEWHERE — an unrecognised (or
 * missing) source goes to 'other', never to nowhere. This page used to filter
 * server-side on source instead, against a union of the only two values it
 * writes, so all 8 live bekesher tickets ('ai' ×5, 'mobile-app' ×3) matched
 * neither tab and both lists rendered empty.
 */
function bucketOf(t: Ticket): TabKey {
  const s = sourceOf(t);
  if (s === 'internal') return 'internal';
  if (CUSTOMER_SOURCES.has(s)) return 'customer';
  return 'other';
}

/**
 * The doc vocabulary is UPPERCASE priority / lowercase status. Rows written by
 * another console (or an older one) can arrive in either case, and a value that
 * matches no <option> renders as an EMPTY select — i.e. the row looks like it
 * has no status at all. Pin both to the known sets on the way in.
 */
function normalize(t: Ticket): Ticket {
  const p = String(t.priority ?? '').toUpperCase() as TicketPriority;
  const s = String(t.status ?? '').toLowerCase() as TicketStatus;
  return {
    ...t,
    priority: PRIORITIES.includes(p) ? p : 'MEDIUM',
    status: STATUSES.includes(s) ? s : 'open',
  };
}

export function TicketsPage() {
  const { user, claims, signOut } = useAuth();
  const tenantCode = claims?.tenantCode;
  const agentUid = user?.uid ?? '';

  // The internal list is the stated need, so it is what opens.
  const [tab, setTab] = useState<TabKey>('internal');
  const [status, setStatus] = useState<TicketStatus | ''>('');
  const [assignee, setAssignee] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [agents, setAgents] = useState<DirectoryAgent[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [showCreate, setShowCreate] = useState(false);

  // uid → display name, so rows can name their owner.
  useEffect(() => {
    if (!tenantCode) return;
    let cancelled = false;
    directoryApi
      .listAgentsDirectory(tenantCode)
      .then((r) => {
        if (!cancelled) setAgents(r.items);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [tenantCode]);

  const agentNames = useMemo(() => {
    const m: Record<string, string> = {};
    for (const a of agents) m[a.uid] = a.displayName;
    return m;
  }, [agents]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebouncedSearch(search.trim()), 250);
    return () => window.clearTimeout(id);
  }, [search]);

  // Status / owner / search stay server-side (they are real query filters).
  // `source` deliberately does NOT: it is the one field whose vocabulary other
  // services extend without telling us, and filtering on it is what made every
  // live ticket invisible. One fetch, bucketed below, so a value we have never
  // seen is a row in "Other" instead of a row that does not exist.
  const load = useCallback(async () => {
    if (!tenantCode) return;
    setLoading(true);
    setLoadError(null);
    try {
      const r = await api.listTickets(tenantCode, {
        status: status || undefined,
        assigneeUid: assignee || undefined,
        q: debouncedSearch || undefined,
        limit: 200,
      });
      setTickets((r.tickets ?? []).map(normalize));
    } catch (e) {
      setTickets([]);
      setLoadError(errorMessage(e));
    } finally {
      setLoading(false);
    }
  }, [tenantCode, status, assignee, debouncedSearch]);

  useEffect(() => {
    void load();
  }, [load]);

  // Counts for EVERY tab, not just the open one: "there are 8 tickets in the
  // other list" has to be visible without clicking into it.
  const counts = useMemo(() => {
    const c: Record<TabKey, number> = { internal: 0, customer: 0, other: 0 };
    for (const t of tickets) c[bucketOf(t)] += 1;
    return c;
  }, [tickets]);

  const visible = useMemo(() => tickets.filter((t) => bucketOf(t) === tab), [tickets, tab]);

  // One control at a time, applied locally on success so the row doesn't blink.
  // A change to the very field the list is filtered on can push the row out of
  // the current query, so that case refetches instead of leaving a liar on screen.
  async function patchTicket(
    t: Ticket,
    body: { status?: TicketStatus; assigneeUid?: string; priority?: TicketPriority; comment?: string },
    local?: Partial<Ticket>
  ): Promise<boolean> {
    if (!tenantCode) return false;
    setBusyId(t.id);
    setLoadError(null);
    try {
      await api.updateTicket(tenantCode, t.id, body);
      const conflicts =
        (body.status !== undefined && status !== '') ||
        (body.assigneeUid !== undefined && assignee !== '');
      if (conflicts) {
        await load();
      } else if (local) {
        setTickets((cur) => cur.map((x) => (x.id === t.id ? { ...x, ...local } : x)));
      }
      return true;
    } catch (e) {
      setLoadError(errorMessage(e));
      return false;
    } finally {
      setBusyId(null);
    }
  }

  async function addNote(t: Ticket) {
    const text = note.trim();
    if (!text) return;
    // Keep the draft on failure — the agent typed it, losing it is worse than
    // the error banner they're about to read.
    if (!(await patchTicket(t, { comment: text }))) return;
    setNote('');
    setNotice('Note added.');
  }

  function toggleExpanded(id: string) {
    setExpandedId((cur) => (cur === id ? null : id));
    setNote('');
  }

  const activeFilters = status !== '' || assignee !== '' || debouncedSearch !== '';

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar tenantCode={tenantCode ?? ''} userEmail={user?.email ?? undefined} onSignOut={signOut} />

      <div className="pt-10 px-6 pb-6">
        {/* Header row */}
        <div className="flex items-center justify-between py-4">
          <div className="flex items-baseline gap-3">
            <h1 className="text-2xl font-semibold text-gray-900">Tickets</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => void load()}
              disabled={loading}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-50 disabled:opacity-50"
              title="Reload"
            >
              {loading ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <RefreshCw className="w-3.5 h-3.5" />
              )}
            </button>
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700"
              title="Open a tracked issue for the team — no customer needed"
            >
              <Plus className="w-3.5 h-3.5" /> New issue
            </button>
          </div>
        </div>

        {/* Which worklist. Internal first: it is the list that replaced the
            spreadsheet, customer tickets are the secondary view. */}
        <div className="flex items-center gap-2 border-b border-gray-200 pb-2">
          {TABS.map((tabDef) => {
            const isSel = tab === tabDef.key;
            const n = counts[tabDef.key];
            // "Other" holding rows is a signal, not a normal state — badge it
            // amber so an unrecognised source gets noticed instead of sitting
            // quietly in a tab nobody opens.
            const badge =
              tabDef.key === 'other' && n > 0
                ? 'bg-amber-100 text-amber-800'
                : isSel
                  ? 'bg-primary-50 text-primary-700'
                  : 'bg-gray-100 text-gray-500';
            return (
              <button
                key={tabDef.key}
                onClick={() => setTab(tabDef.key)}
                title={tabDef.hint}
                className={`px-3 py-1 text-sm font-medium -mb-2.5 border-b-2 ${
                  isSel
                    ? 'text-gray-900 border-primary-600'
                    : 'text-gray-500 border-transparent hover:text-gray-700'
                }`}
              >
                {tabDef.label}
                <span className={`ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded ${badge}`}>
                  {loading ? '…' : n}
                </span>
              </button>
            );
          })}
        </div>

        {/* Toolbar row */}
        <div className="flex items-center gap-2 flex-wrap mt-3 text-xs">
          <div className="relative w-64">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search tickets (number / subject / customer)"
              className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded bg-white focus:outline-none focus:ring-1 focus:ring-primary-500"
            />
          </div>
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as TicketStatus | '')}
            className={`px-2 py-1.5 border rounded ${
              status
                ? 'bg-primary-50 border-primary-300 text-primary-700 font-medium'
                : 'bg-white border-gray-200 text-gray-700'
            }`}
            title="Filter by status"
          >
            <option value="">Any status</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <select
            value={assignee}
            onChange={(e) => setAssignee(e.target.value)}
            className={`px-2 py-1.5 border rounded max-w-[200px] ${
              assignee
                ? 'bg-primary-50 border-primary-300 text-primary-700 font-medium'
                : 'bg-white border-gray-200 text-gray-700'
            }`}
            title="Filter by owner"
          >
            <option value="">Anyone</option>
            {agentUid && <option value={agentUid}>Mine</option>}
            {agents
              .filter((a) => a.uid !== agentUid)
              .map((a) => (
                <option key={a.uid} value={a.uid}>
                  {a.displayName}
                </option>
              ))}
          </select>
          {activeFilters && (
            <button
              onClick={() => {
                setStatus('');
                setAssignee('');
                setSearch('');
              }}
              className="px-2.5 py-1.5 text-gray-600 bg-white border border-gray-200 rounded hover:bg-gray-50"
            >
              Clear filters
            </button>
          )}
        </div>

        {notice && (
          <div className="mt-3 px-3 py-2 text-xs text-green-800 bg-green-50 border border-green-200 rounded flex items-center justify-between gap-3">
            <span>{notice}</span>
            <button onClick={() => setNotice(null)} className="text-green-700 hover:text-green-900">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
        {loadError && (
          <div className="mt-3 px-3 py-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded">
            {loadError}
          </div>
        )}

        {/* Table */}
        <div className="mt-4 bg-white border border-gray-200 rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-gray-500">
                <th className="w-8 px-3 py-2" />
                <th className="px-3 py-2 text-left font-medium text-xs uppercase tracking-wide">Ticket</th>
                <th className="px-3 py-2 text-left font-medium text-xs uppercase tracking-wide">
                  {tab === 'internal' ? 'What' : 'Subject'}
                </th>
                <th className="px-3 py-2 text-left font-medium text-xs uppercase tracking-wide">Priority</th>
                <th className="px-3 py-2 text-left font-medium text-xs uppercase tracking-wide">Status</th>
                <th className="px-3 py-2 text-left font-medium text-xs uppercase tracking-wide">Owner</th>
                <th className="px-3 py-2 text-left font-medium text-xs uppercase tracking-wide">Updated</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((t) => {
                const isOpen = expandedId === t.id;
                const rowBusy = busyId === t.id;
                const owner = assigneeOf(t);
                // Where the row came from, whenever that is not just "an agent
                // opened it here". Bucketing 'ai' / 'mobile-app' under Customer
                // tickets must not cost the provenance.
                const src = sourceOf(t);
                const srcLabel =
                  src && src !== 'customer' && src !== 'internal' ? (SOURCE_LABELS[src] ?? src) : null;
                return (
                  <Fragment key={t.id}>
                    <tr
                      className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${
                        rowBusy ? 'opacity-60' : ''
                      }`}
                      onClick={() => toggleExpanded(t.id)}
                    >
                      <td className="px-3 py-2.5 text-gray-400">
                        {rowBusy ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : isOpen ? (
                          <ChevronDown className="w-3.5 h-3.5" />
                        ) : (
                          <ChevronRight className="w-3.5 h-3.5" />
                        )}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-xs font-mono text-gray-700" dir="ltr">
                          {t.ticketNumber || t.id.slice(0, 8)}
                        </div>
                        <div className="text-[11px] text-gray-400">{formatRelative(t.createdAt)}</div>
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-[420px]" dir="auto">
                          {t.subject}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5">
                          {t.category && (
                            <span className="text-[10px] uppercase tracking-wide text-gray-500">
                              {pretty(t.category)}
                            </span>
                          )}
                          {t.area && (
                            <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">
                              {t.area}
                            </span>
                          )}
                          {srcLabel && (
                            <span
                              className="text-[10px] px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200"
                              title={`Ticket source: ${t.source}`}
                            >
                              {srcLabel}
                            </span>
                          )}
                          {(t.customerName || t.customerEmail) && (
                            <span className="text-[11px] text-gray-500 truncate max-w-[200px]" dir="auto">
                              {t.customerName || t.customerEmail}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={t.priority}
                          disabled={rowBusy}
                          onChange={(e) => {
                            const p = e.target.value as TicketPriority;
                            void patchTicket(t, { priority: p }, { priority: p });
                          }}
                          className={`text-xs border rounded px-1.5 py-1 ${PRIORITY_STYLES[t.priority]}`}
                        >
                          {PRIORITIES.map((p) => (
                            <option key={p} value={p}>
                              {pretty(p)}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={t.status}
                          disabled={rowBusy}
                          onChange={(e) => {
                            const s = e.target.value as TicketStatus;
                            void patchTicket(t, { status: s }, { status: s });
                          }}
                          className={`text-xs border rounded px-1.5 py-1 ${STATUS_STYLES[t.status]}`}
                        >
                          {STATUSES.map((s) => (
                            <option key={s} value={s}>
                              {STATUS_LABELS[s]}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <select
                          value={owner}
                          disabled={rowBusy}
                          onChange={(e) => {
                            const uid = e.target.value;
                            void patchTicket(
                              t,
                              // '' clears the owner — the PATCH contract has no
                              // null form, so an empty uid is the only way to say
                              // "nobody" and the server decides whether it takes.
                              { assigneeUid: uid },
                              { assignedToId: uid, assigneeUid: uid, assignedTo: agentNames[uid] ?? null }
                            );
                          }}
                          className="text-xs border border-gray-300 rounded px-1.5 py-1 bg-white text-gray-700 max-w-[160px]"
                        >
                          <option value="">Unassigned</option>
                          {/* An owner who has left the directory still needs a
                              matching option or the select would show the wrong
                              person. */}
                          {owner && !agentNames[owner] && (
                            <option value={owner}>{t.assignedTo || owner}</option>
                          )}
                          {agents.map((a) => (
                            <option key={a.uid} value={a.uid}>
                              {a.uid === agentUid ? `${a.displayName} (me)` : a.displayName}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                        {formatRelative(t.updatedAt)}
                      </td>
                    </tr>
                    {isOpen && (
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <td />
                        <td colSpan={6} className="px-3 py-3">
                          {t.description ? (
                            <div className="text-xs text-gray-700 whitespace-pre-wrap mb-2" dir="auto">
                              {t.description}
                            </div>
                          ) : (
                            <div className="text-xs text-gray-400 mb-2">
                              The opening description lives with the ticket's messages and isn't
                              carried on the list row.
                            </div>
                          )}
                          {t.resolutionNotes && (
                            <div className="text-xs text-gray-700 mb-2" dir="auto">
                              <span className="font-medium">Resolution:</span> {t.resolutionNotes}
                            </div>
                          )}
                          <div className="flex items-start gap-2">
                            <textarea
                              value={note}
                              onChange={(e) => setNote(e.target.value)}
                              rows={2}
                              placeholder="Add a note to this ticket…"
                              className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs bg-white"
                            />
                            <button
                              onClick={() => void addNote(t)}
                              disabled={busyId === t.id || !note.trim()}
                              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 disabled:opacity-50"
                            >
                              <StickyNote className="w-3.5 h-3.5" /> Add note
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
              {visible.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-8 text-center text-xs text-gray-500">
                    {loading ? (
                      'Loading…'
                    ) : (
                      <>
                        <div>
                          {activeFilters
                            ? 'No tickets in this list match these filters.'
                            : tab === 'internal'
                              ? 'No team issues yet. "New issue" opens the first one.'
                              : tab === 'customer'
                                ? 'No customer tickets yet.'
                                : 'Nothing here — every ticket has a source this console recognises.'}
                        </div>
                        {/* An empty list next to a non-empty one is where the
                            rows went. Say so, rather than letting it read as
                            "there are no tickets". */}
                        {tickets.length > 0 && (
                          <div className="mt-1 text-gray-400">
                            {TABS.filter((x) => x.key !== tab && counts[x.key] > 0)
                              .map((x) => `${counts[x.key]} in ${x.label}`)
                              .join(' · ')}
                          </div>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && tenantCode && (
        <CreateTicketModal
          tenantCode={tenantCode}
          agentUid={agentUid}
          source="internal"
          agents={agents}
          onClose={(msg) => {
            setShowCreate(false);
            if (!msg) return;
            setNotice(msg);
            // A new issue is always internal, so show the list it landed in.
            // The fetch is source-agnostic now, so the reload is needed either
            // way — switching tabs alone would only re-bucket a stale list.
            setTab('internal');
            void load();
          }}
        />
      )}
    </div>
  );
}
